import { graphql } from '../gql';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { OutlinedInput, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { ModalContent } from '../modal/components/ModalContent';
import { ModalActions } from '../modal/components/ModalActions';
import { useModalContext } from '../modal/ModalContext';
import { FormField } from '../form/FormField';
import { useTypedForm } from '../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { useSnackbar } from '../store/snackbar';

const deletePersonaMutation = graphql(`
	mutation persona_deletePersona($id: UUID!) {
		deletePersona(by: { id: $id }) {
			ok
			errorMessage
		}
	}
`);

const getPersonaQuery = graphql(`
	query persona_getPersonaById($id: UUID!) {
		getPersona(by: { id: $id }) {
			activities {
				id
			}
		}
	}
`);

const createFormSchema = (textToMatch: string, intl: IntlShape) =>
	z.object({
		confirmField: z.literal(textToMatch, {
			errorMap: () => ({ message: intl.formatMessage({ id: 'common.delete.invalid' }, { desired: textToMatch }) }),
		}),
	});

export const deletePersonaModalSchema = z.object({
	id: z.string(),
	name: z.string(),
});

export const DeletePersonaModal: FC<z.infer<typeof deletePersonaModalSchema>> = ({ id, name }) => {
	const { openSnackbar } = useSnackbar();
	const { mutate: deletePersona } = useApiMutation(deletePersonaMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.deletePersona.ok,
				toastMessages: { error: intl.formatMessage({ id: 'persona.delete.error' }) },
				errorMessage: result.deletePersona.errorMessage,
				openSnackbar,
			});
		},
	});
	const { data } = useApiQuery(getPersonaQuery, { variables: { id } });
	const { closeModal } = useModalContext();
	const navigate = useNavigate();
	const intl = useIntl();
	const form = useTypedForm({
		schema: createFormSchema(name, intl),
	});
	const requiresConfirmation = data?.getPersona ? data.getPersona.activities.length > 0 : false;

	return (
		<>
			<ModalContent
				isDelete
				requiresConfirmation={requiresConfirmation}
				title={intl.formatMessage({ id: 'persona.delete.confirmation' }, { name })}
				description={''}
			>
				<>
					<Typography textAlign={'center'}>
						<strong>
							<FormattedMessage id={'common.delete.confirmation'} />
						</strong>
					</Typography>
					<Stack width={'50%'} alignSelf={'center'}>
						<form noValidate>
							<FormField
								form={form}
								name={'confirmField'}
								label={''}
								render={(props) => (
									<OutlinedInput {...props} placeholder={intl.formatMessage({ id: 'common.delete.confirmation-placeholder' }, { name })} />
								)}
							/>
						</form>
					</Stack>
				</>
			</ModalContent>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deletePersona({ id });
					closeModal();
					navigate('/personas');
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
				disablePrimaryButton={requiresConfirmation ? !form.formState.isValid : false}
			/>
		</>
	);
};
